import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { expensesApi, extractOne } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const ExpenseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [sub_total_amount, setSubTotalAmount] = useState<string>('');
  const [tax_amount, setTaxAmount] = useState<string>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [inv_date, setInvDate] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading } = useQuery({
    queryKey: ['expenses', numId],
    queryFn: () => expensesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    const r = row as any;
    if (r.sub_total_amount != null) setSubTotalAmount(String(r.sub_total_amount));
    if (r.tax_amount != null) setTaxAmount(String(r.tax_amount));
    if (r.name != null) setName(String(r.name));
    if (r.remarks != null) setDescription(String(r.remarks));
    if (r.inv_date != null) setInvDate(String(r.inv_date).slice(0, 10));
    if (r.is_active != null) setIsActive(r.is_active === 'No' ? 'No' : 'Yes');
  }, [row]);

  const numericSubTotal = parseFloat(sub_total_amount) || 0;
  const numericTax = tax_amount === '' ? 0 : parseFloat(tax_amount) || 0;
  const computedTotal = numericSubTotal + numericTax;

  const update = useMutation({
    mutationFn: () =>
      expensesApi.update(numId, {
        name: name || undefined,
        description: description || undefined,
        sub_total_amount: sub_total_amount === '' ? undefined : numericSubTotal,
        tax_amount: tax_amount === '' ? undefined : numericTax,
        inv_date: inv_date || undefined,
        is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      toast.success('Expense updated.');
      navigate(`/dashboard/expenses/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Expense</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Update basic fields; total amount is computed from sub total and tax.</p>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Expense name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label="Invoice date">
            <input className={inputClass} type="date" value={inv_date} onChange={(e) => setInvDate(e.target.value)} />
          </FormField>
          <FormField label="Sub total amount (MWK)">
            <input className={inputClass} type="number" step="0.01" min={0} value={sub_total_amount} onChange={(e) => setSubTotalAmount(e.target.value)} />
          </FormField>
          <FormField label="Tax amount (MWK)">
            <input className={inputClass} type="number" step="0.01" min={0} value={tax_amount} onChange={(e) => setTaxAmount(e.target.value)} />
          </FormField>
          <FormField label="Total amount (computed)">
            <input className={inputClass} type="number" step="0.01" min={0} value={computedTotal.toFixed(2)} readOnly />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Description">
              <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Active">
            <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
          <div className="md:col-span-2 flex gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ExpenseEditPage;
